import { useState, useEffect, useRef } from "react";
import "./dropDown.scss";
import SortIcons from "constants/Homepage/ysafe/iconFunctions/sortIcons";
import Drawer from "components/Drawer/index";
import useScreenMobile from "hooks/useScreen";

const Dropdown = ({
  options,
  placeholder,
  arrowColor,
  background,
  textColor,
  itemColor,
  hoveredColor,
  selected,
  setSelected,
  forceOpen = false
}) => {
  // const [selected, setSelected] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [direction, setDirection] = useState("down");
  const isMobile = useScreenMobile({ size: 992 });

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      if (!forceOpen) {
        setIsOpen(false);
      }
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || isMobile) return;
    const computeMenuFit = () => {
      try {
        const inputRect = dropdownRef.current.getBoundingClientRect();
        // try to find modal container that should bound the dropdown
        const modalEl = dropdownRef.current.closest('.SecurityQuestionsMain') || document.documentElement;
        const modalRect = modalEl.getBoundingClientRect();
        const margin = 8;
        const defaultMax = 260;
        const minAcceptable = 120;
        const availableBelow = Math.max(0, modalRect.bottom - inputRect.bottom - margin);
        const availableAbove = Math.max(0, inputRect.top - modalRect.top - margin);

        if (availableBelow < minAcceptable && availableAbove > availableBelow) {
          setDirection('up');
          setMenuStyle({ maxHeight: `${Math.min(defaultMax, availableAbove)}px` });
        } else {
          setDirection('down');
          const mh = Math.min(defaultMax, Math.max(minAcceptable, availableBelow || defaultMax));
          setMenuStyle({ maxHeight: `${mh}px` });
        }
      } catch (e) {
        setDirection('down');
        setMenuStyle({ maxHeight: '260px' });
      }
    };

    computeMenuFit();
    window.addEventListener('resize', computeMenuFit);
    window.addEventListener('scroll', computeMenuFit, true);
    return () => {
      window.removeEventListener('resize', computeMenuFit);
      window.removeEventListener('scroll', computeMenuFit, true);
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (!isOpen) {
      setHoveredIndex(null);
    } else if (selected) {
      const idx = options.indexOf(selected);
      if (idx !== -1) {
        setHoveredIndex(idx);
      }
    }
  }, [isOpen, selected, options]);

  useEffect(() => {
    if (isOpen && menuRef.current && hoveredIndex !== null) {
      const menuEl = menuRef.current;
      const hoveredEl = menuEl.children[hoveredIndex];
      if (hoveredEl) {
        const menuRect = menuEl.getBoundingClientRect();
        const hoveredRect = hoveredEl.getBoundingClientRect();

        if (hoveredRect.bottom > menuRect.bottom) {
          menuEl.scrollTop += hoveredRect.bottom - menuRect.bottom;
        } else if (hoveredRect.top < menuRect.top) {
          menuEl.scrollTop -= menuRect.top - hoveredRect.top;
        }
      }
    }
  }, [hoveredIndex, isOpen]);

  const handleOptionClick = (option) => {
    setSelected(option);
    setIsOpen(false);
  };

  const handleMouseEnter = (index) => setHoveredIndex(index);
  const handleMouseLeave = () => setHoveredIndex(null);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
      if (isOpen && hoveredIndex !== null && hoveredIndex >= 0 && hoveredIndex < options.length) {
        handleOptionClick(options[hoveredIndex]);
      } else {
        setIsOpen((prev) => !prev);
      }
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      event.stopPropagation();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setHoveredIndex((prevIndex) => {
          if (prevIndex === null || prevIndex >= options.length - 1) {
            return 0;
          }
          return prevIndex + 1;
        });
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setHoveredIndex((prevIndex) => {
          if (prevIndex === null || prevIndex <= 0) {
            return options.length - 1;
          }
          return prevIndex - 1;
        });
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(false);
    } else if (event.key === "Tab") {
      setIsOpen(false);
    }
  };

  const renderOptions = () =>
    options.map((option, index) => (
      <div
        key={index}
        className="dropdown-item"
        style={{
          color: itemColor,
          backgroundColor: hoveredIndex === index ? hoveredColor : "transparent"
        }}
        onMouseEnter={() => handleMouseEnter(index)}
        onMouseLeave={handleMouseLeave}
        onClick={() => handleOptionClick(option)}
      >
        {option}
      </div>
    ));

  return (
    <div
      className="dropdown"
      ref={dropdownRef}
      onClick={() => setIsOpen(!isOpen)}
      style={{ backgroundColor: background }}
    >
      <input
        type="text"
        value={selected}
        readOnly
        className="dropdown-input"
        placeholder={placeholder}
        style={{ backgroundColor: background, color: textColor }}
        onKeyDown={handleKeyDown}
      />
      <div className={`arrowWrap ${isOpen ? "rotate" : ""}`}>
        <SortIcons
          type="arrow"
          color={arrowColor}
          onClick={() => setIsOpen(false)}
        />
      </div>
      {isOpen && (
        <div
          className={`dropdown-menu ${isMobile ? "mobile" : ""} ${direction === 'up' ? 'up' : ''}`}
          ref={menuRef}
          style={{ backgroundColor: background, ...menuStyle }}
        >
          {isMobile ? (
            <Drawer
              isVisible={isOpen}
              showFooter={false}
              showHeader={false}
              mockClass="dropdownDrawer"
            >
              <div className="dropdownDrawerContent">
                {renderOptions()}
              </div>
            </Drawer>
          ) : (
            renderOptions()
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
